
import api, {
  buildActivityScopeHeaders,
  buildCompanyScopeHeaders,
} from "./api";


function requireNonEmptyString(
  value,
  fieldName,
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    throw new TypeError(
      `${fieldName} must be a non-empty string.`,
    );
  }

  return value.trim();
}


function buildSupplierContactsBasePath(
  supplierUuid,
) {
  const normalizedSupplierUuid =
    requireNonEmptyString(
      supplierUuid,
      "supplierUuid",
    );

  return `/supply-chain/suppliers/${encodeURIComponent(
    normalizedSupplierUuid,
  )}/contacts`;
}


function buildSupplierContactPath(
  supplierUuid,
  contactUuid,
) {
  const normalizedContactUuid =
    requireNonEmptyString(
      contactUuid,
      "contactUuid",
    );

  return `${buildSupplierContactsBasePath(
    supplierUuid,
  )}/${encodeURIComponent(
    normalizedContactUuid,
  )}`;
}


function buildSupplierContactScopeConfig({
  companyUuid,
  activityUuid,
} = {}) {
  const normalizedActivityUuid =
    requireNonEmptyString(
      activityUuid,
      "activityUuid",
    );

  return {
    headers: {
      ...buildCompanyScopeHeaders(
        companyUuid,
      ),
      ...buildActivityScopeHeaders(
        normalizedActivityUuid,
      ),
    },
  };
}


export function getSupplierContactErrorMessage(
  error,
  fallback = "Unable to process the supplier contact request.",
) {
  const responseData =
    error?.response?.data;

  if (
    typeof responseData?.message
      === "string"
    && responseData.message.trim()
  ) {
    return responseData.message.trim();
  }

  if (
    typeof responseData?.error?.message
      === "string"
    && responseData.error.message.trim()
  ) {
    return (
      responseData.error.message.trim()
    );
  }

  if (
    typeof responseData?.error
      === "string"
    && responseData.error.trim()
  ) {
    return responseData.error.trim();
  }

  if (
    typeof error?.message === "string"
    && error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
}


export async function listSupplierContacts(
  supplierUuid,
  {
    companyUuid,
    activityUuid,
  } = {},
) {
  const response = await api.get(
    buildSupplierContactsBasePath(
      supplierUuid,
    ),
    buildSupplierContactScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );

  return response.data;
}


export async function getSupplierContact(
  supplierUuid,
  contactUuid,
  {
    companyUuid,
    activityUuid,
  } = {},
) {
  const response = await api.get(
    buildSupplierContactPath(
      supplierUuid,
      contactUuid,
    ),
    buildSupplierContactScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );

  return response.data;
}


export async function createSupplierContact(
  supplierUuid,
  payload,
  {
    companyUuid,
    activityUuid,
  } = {},
) {
  const response = await api.post(
    buildSupplierContactsBasePath(
      supplierUuid,
    ),
    payload,
    buildSupplierContactScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );

  return response.data;
}


export async function updateSupplierContact(
  supplierUuid,
  contactUuid,
  payload,
  {
    companyUuid,
    activityUuid,
  } = {},
) {
  const response = await api.patch(
    buildSupplierContactPath(
      supplierUuid,
      contactUuid,
    ),
    payload,
    buildSupplierContactScopeConfig({
      companyUuid,
      activityUuid,
    }),
  );

  return response.data;
}


export {
  buildSupplierContactPath,
  buildSupplierContactsBasePath,
};
